import { useSettings } from '../../hooks/queries'

/**
 * Datenschutzhinweise — kurz, förmlich, in der üblichen Gliederung.
 *
 * Aus dem Betrieb, in zwei Schritten. Zuerst: „Die Texte sind sperrig in einem
 * schlechten Deutsch." Dann, auf die Rückfrage nach der Richtung: „kürzer,
 * förmlicher, juristisch einwandfrei." Hier stand vorher eine erzählende
 * Fassung im Ton der übrigen Anwendung — die ist für eine Datenschutzerklärung
 * die falsche Gattung. Sie gliedert sich nach Art. 13 DSGVO: Verantwortlicher,
 * Zwecke und Rechtsgrundlagen, Empfänger, Speicherdauer, Rechte.
 *
 * **Die ausführliche Fassung bleibt daneben bestehen**
 * (`docs/datenschutz-leser.md`) und tut etwas anderes: sie begründet, warum es
 * die drei Ausnahmen gibt. Eine Erklärung ist keine Erklärung im Rechtssinn,
 * und umgekehrt.
 *
 * **Und sie sagt die Wahrheit über *diese* Installation**, nicht über die
 * Software im Allgemeinen: die Empfänger stehen nur da, wenn ihr Zugang
 * wirklich eingerichtet ist (`services` aus `/api/settings`). Ein fester Text
 * benennte sonst eine Übermittlung, die gar nicht stattfindet — bei einer
 * Datenschutzerklärung ist das kein Schönheitsfehler.
 *
 * **Die Rechtsgrundlagen sind die Einschätzung des Betreibers.** Die Anwendung
 * schreibt sie hin, weil sie ihre eigene Verarbeitung kennt; verantwortlich
 * dafür ist, wer betreibt.
 *
 * **Heute sieht die Kategorie nur die Verwaltung** (`SettingsPage`). Das ist
 * eine Stufe und keine Endfassung. Zum Aufmachen sind es zwei Griffe — die
 * Bedingung um die Kategorie fällt, und im Server wandert `operator` vor das
 * `if (!$istVerwaltung)`. `tests/BetreiberTest.php` hält die heutige Stufe fest
 * und schlägt dann fehl; das ist Absicht.
 */
export function Datenschutz() {
  const settings = useSettings()
  const dienste = settings.data?.services
  const apple = dienste?.place === true || dienste?.weather === true
  /*
   * **Solange die Antwort unterwegs ist, wird hier nichts behauptet.**
   *
   * Zwei der Empfänger stehen nur da, wenn ihr Zugang eingerichtet ist — und
   * ehe der Server das gesagt hat, sähe die Seite aus wie eine Installation,
   * in der sie abgeschaltet sind. Dasselbe gilt für den Verantwortlichen:
   * „noch nicht eingetragen" wäre in diesem Augenblick keine Auskunft,
   * sondern eine falsche.
   */
  const laedt = settings.data === undefined

  return (
    <>
      <Verantwortlicher
        betreiber={settings.data?.operator}
        darfEinrichten={settings.data?.ceiling !== undefined}
        laedt={laedt}
      />

      <div className="card card--seite">
        <h2 className="card__titel">Grundsatz</h2>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Antworten, Reaktionen und Lesebestätigungen sieht nur die Verfasserin
          oder der Verfasser des Reiseberichts. Andere Empfänger sehen diese
          Inhalte nicht. Diese Beschränkung ist fest in der App eingebaut und
          lässt sich nicht ändern.
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Verarbeitete Daten, Zwecke, Rechtsgrundlagen</h2>

        <p className="schublade">
          <span className="schublade__name">Bestandsdaten</span>
          <span className="schublade__text">
            E-Mail-Adresse, Anzeigename, Passwort-Hashwert und
            Registrierungszeitpunkt; bei Nutzung eines Passkeys dessen
            öffentlicher Schlüssel, ein Gerätename sowie Anlege- und
            Nutzungszeitpunkt. Zweck: Einrichtung und Führung des
            Nutzerkontos. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Inhaltsdaten</span>
          <span className="schublade__text">
            Nachrichten, Fotos, Videos, Sprachnachrichten, GIFs, Reaktionen und
            Lesestatus mit Zeitstempel. Zweck: Erbringung der Dienstleistung.
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Standortdaten</span>
          <span className="schublade__text">
            Nur Routen und Ortsangaben, die die verfassende Person selbst
            hochlädt. Empfänger übermitteln keinen eigenen Standort. Eine Ortung
            im Hintergrund findet nicht statt.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Nutzungsdaten</span>
          <span className="schublade__text">
            Ein technisch notwendiges Anmelde-Cookie und eine Kurzangabe zum
            verwendeten Browser. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO und
            § 25 Abs. 2 Nr. 2 TDDDG. Cookies zu Werbe- oder Analysezwecken
            kommen nicht zum Einsatz.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Sicherheitsdaten</span>
          <span className="schublade__text">
            Anzahl fehlgeschlagener Anmeldeversuche. Zweck: Schutz vor
            unbefugtem Zugriff. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO. Das
            berechtigte Interesse liegt im Schutz des Nutzerkontos vor
            Missbrauch.
          </span>
        </p>
        <p className="schublade">
          <span className="schublade__name">Push-Benachrichtigungen</span>
          <span className="schublade__text">
            Nur nach ausdrücklicher Freigabe verarbeitet die App die
            Zustelladresse des Endgeräts und den zugehörigen Schlüssel.
            Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO. Die Einwilligung ist
            jederzeit mit Wirkung für die Zukunft widerrufbar (Art. 7 Abs. 3
            DSGVO).
          </span>
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Speicherdauer</h2>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Bestands- und Inhaltsdaten speichert die App bis zur Löschung durch
          die betroffene Person oder bis zur Kontolöschung. Inhalte innerhalb
          eines Reiseberichts bleiben beim Empfänger, bis dieser sie löscht.
          Anmeldedaten der Sitzung speichert die App bis zur Abmeldung oder bis
          die Sitzung abläuft. Push-Zustelladressen speichert die App bis zum
          Widerruf oder bis der Push-Dienst die Zustellung dauerhaft ablehnt.
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Empfänger und Drittlandübermittlung</h2>

        <p className="card__lead" style={{ marginBottom: 12 }}>
          Die App nutzt für einzelne Funktionen externe Dienste.
          {(apple || dienste?.gif === true)
            && ' Bei diesen Diensten kann eine Übermittlung personenbezogener'
              + ' Daten in die USA stattfinden.'}
        </p>

        <p className="schublade">
          <span className="schublade__name">Kartendarstellung</span>
          <span className="schublade__text">
            Das Endgerät ruft Kartenausschnitte direkt bei OpenStreetMap bzw.
            OpenTopoMap ab. Dabei übermittelt es die IP-Adresse, den
            angeforderten Kartenausschnitt und den Namen dieser Seite. Inhalts-
            und Bestandsdaten übermittelt die App dabei nicht. Rechtsgrundlage:
            Art. 6 Abs. 1 lit. f DSGVO. Das berechtigte Interesse liegt in der
            technischen Bereitstellung der Kartenfunktion.
          </span>
        </p>

        {laedt && (
          <p className="schublade">
            <span className="schublade__text">Einen Moment …</span>
          </p>
        )}

        {/* Nur, wo es wirklich läuft: eine benannte Übermittlung, die gar nicht
            stattfindet, wäre in einer Datenschutzerklärung ein Fehler. */}
        {apple && (
          <p className="schublade">
            <span className="schublade__name">Ortsname und Wetter</span>
            <span className="schublade__text">
              Der Server ruft für Ortsnamen und Wetterangaben Dienste von Apple
              ab (Maps Server API, WeatherKit). Dabei übermittelt er Koordinaten
              und Zeitpunkt des jeweiligen Punktes einmalig sowie die IP-Adresse
              des Servers. Für eine Wetterkarte im Chat übermittelt er die
              Koordinaten des Geräts der verfassenden Person im Moment des
              Antippens. Nicht das Endgerät der Empfänger löst diese Abrufe
              aus, sondern ausschließlich die verfassende Person. Eine
              Übermittlung in die USA ist möglich. Rechtsgrundlage: Art. 6
              Abs. 1 lit. f DSGVO. Die Übermittlung selbst stützt sich auf
              EU-Standardvertragsklauseln nach Art. 46 Abs. 2 lit. c DSGVO, die
              Apple für Datentransfers aus der EU verwendet.
            </span>
          </p>
        )}

        {dienste?.gif === true && (
          <p className="schublade">
            <span className="schublade__name">GIFs in Antworten</span>
            <span className="schublade__text">
              Bei der GIF-Suche übermittelt der Server nur den Suchbegriff an
              Giphy, nicht die IP-Adresse des Endgeräts. Vorschaubilder ruft das
              Endgerät direkt bei Giphy ab, solange die Auswahl geöffnet ist.
              Das ausgewählte GIF speichert die App anschließend auf dem eigenen
              Server. Beim späteren Betrachten findet keine erneute Übermittlung
              an Giphy statt. Eine Übermittlung in die USA ist möglich.
              Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO. Die Übermittlung
              selbst stützt sich auf den Angemessenheitsbeschluss der
              EU-Kommission nach Art. 45 DSGVO, da Giphy nach dem
              EU-U.S. Data Privacy Framework zertifiziert ist.
            </span>
          </p>
        )}

        <p className="schublade">
          <span className="schublade__name">Push-Zustellung</span>
          <span className="schublade__text">
            Freigegebene Benachrichtigungen stellt der Push-Dienst des
            jeweiligen Geräteherstellers zu. Dieser Dienst erhält Kenntnis vom
            Zustellvorgang. Der Inhalt bleibt zwischen Server und Endgerät
            verschlüsselt.
          </span>
        </p>

        <p className="dienst__hinweis">
          Eine Weitergabe personenbezogener Daten an weitere Dritte außer den
          genannten Diensten findet nicht statt.
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Auftragsverarbeitung</h2>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Der Anbieter des Webhostings verarbeitet Daten als Auftragsverarbeiter
          im Sinne von Art. 28 DSGVO. Mit ihm besteht ein Vertrag zur
          Auftragsverarbeitung. Dieser Vertrag verpflichtet den Hoster, Daten
          nur nach Weisung des Verantwortlichen zu verarbeiten und angemessene
          Sicherheitsmaßnahmen einzuhalten.
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Technische und organisatorische Maßnahmen</h2>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Der Verantwortliche setzt technische und organisatorische Maßnahmen
          nach Art. 32 DSGVO ein, um personenbezogene Daten zu schützen. Dazu
          zählen die Verschlüsselung der Datenübertragung, Zugriffsbeschränkungen
          auf Serverebene und der Schutz von Passwörtern durch nicht umkehrbare
          Hashwerte. Bei Push-Benachrichtigungen bleibt der Inhalt zwischen
          Server und Endgerät verschlüsselt.
        </p>
      </div>

      <div className="card card--seite">
        <h2 className="card__titel">Was nicht stattfindet</h2>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Die App zeigt keine Werbung, verkauft keine Daten und gibt keine Daten
          an Dritte weiter, außer an die oben genannten Dienste. Sie führt keine
          Reichweiten- oder Nutzungsanalyse durch und setzt keine Zählpixel ein.
          Sie wertet Inhalte weder manuell noch automatisiert aus. Sie trifft
          keine automatisierten Entscheidungen und erstellt keine Profile.
        </p>
      </div>

      <div className="card card--seite" style={{ marginBottom: 0 }}>
        <h2 className="card__titel">Rechte der betroffenen Person</h2>
        <p className="card__lead" style={{ marginBottom: 8 }}>
          Betroffene Personen haben das Recht auf Auskunft (Art. 15 DSGVO),
          Berichtigung (Art. 16 DSGVO), Löschung (Art. 17 DSGVO), Einschränkung
          der Verarbeitung (Art. 18 DSGVO) und Datenübertragbarkeit (Art. 20
          DSGVO). Sie können der Verarbeitung auf Grundlage von Art. 6 Abs. 1
          lit. f DSGVO widersprechen (Art. 21 DSGVO).
        </p>
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Eine erteilte Einwilligung lässt sich jederzeit mit Wirkung für die
          Zukunft widerrufen (Art. 7 Abs. 3 DSGVO). Unabhängig davon besteht ein
          Beschwerderecht bei einer Datenschutz-Aufsichtsbehörde (Art. 77
          DSGVO). Anträge richten Betroffene an den oben genannten
          Verantwortlichen.
        </p>
      </div>
    </>
  )
}

/**
 * Der Verantwortliche — nach Art. 13 Abs. 1 lit. a DSGVO an erster Stelle.
 *
 * **Fehlt der Eintrag, steht das da**, an genau der Stelle, an der die Angabe
 * stünde. Ein leeres Feld wäre die schlechtere Auskunft: es sieht aus wie ein
 * Fehler der Anwendung, und der Leser sucht ihn.
 *
 * Der Kontakt wird **nicht verlinkt, sondern als Text gesetzt**. Was in der
 * `.env` steht, ist ein von Hand getippter Wert; daraus ein `href` zu bauen
 * hieße, fremden Text in ein Attribut zu setzen.
 */
function Verantwortlicher({
  betreiber,
  darfEinrichten,
  laedt,
}: {
  betreiber?: { name: string; address: string; contact: string }
  darfEinrichten: boolean
  /** Die Antwort ist noch unterwegs — dann steht hier weder das eine noch das andere. */
  laedt: boolean
}) {
  const vollstaendig =
    betreiber !== undefined
    && betreiber.name !== ''
    && betreiber.address !== ''
    && betreiber.contact !== ''

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Verantwortlicher</h2>

      {laedt ? (
        <p className="card__lead" style={{ marginBottom: 0 }}>
          Einen Moment …
        </p>
      ) : vollstaendig ? (
        <>
          <p className="card__lead" style={{ marginBottom: 6 }}>
            {betreiber.name}
            <br />
            {betreiber.address}
            <br />
            {betreiber.contact}
          </p>
          <p className="dienst__hinweis">
            Verantwortlicher im Sinne des Art. 4 Nr. 7 DSGVO.
          </p>
        </>
      ) : (
        <>
          <p className="card__lead" style={{ marginBottom: 0 }}>
            Die Angaben zum Verantwortlichen sind noch nicht hinterlegt. Anfragen
            richten Sie bitte an die Person, von der Sie eingeladen wurden.
          </p>

          {/* Schlüsselnamen sind für den Betrieb, nicht für den Leser. Heute
              sieht die Kategorie ohnehin nur die Verwaltung -- die Bedingung
              steht trotzdem da, damit sie beim Aufmachen nicht mitgeht. */}
          {darfEinrichten && (
            <>
              <p className="dienst__hinweis">
                Einzutragen in der <code>.env</code> auf dem Server; ein Neustart
                ist nicht nötig, die Seite zeigt es nach dem nächsten Neuladen.
              </p>
              <ul className="dienst__zeilen">
                <li>OPERATOR_NAME</li>
                <li>OPERATOR_ADDRESS</li>
                <li>OPERATOR_CONTACT</li>
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}
